import type { Theme } from '../types';

export const getThemeColors = (theme: Theme) => {
  const themes = {
    emerald: {
      50: '#ecfdf5',
      100: '#d1fae5',
      200: '#a7f3d0',
      300: '#6ee7b7',
      400: '#34d399',
      500: '#10b981',
      600: '#059669',
      700: '#047857',
      800: '#065f46',
      900: '#064e3b'
    },
    blue: {
      50: '#eff6ff',
      100: '#dbeafe',
      200: '#bfdbfe',
      300: '#93c5fd',
      400: '#60a5fa',
      500: '#3b82f6',
      600: '#2563eb',
      700: '#1d4ed8',
      800: '#1e40af',
      900: '#1e3a8a'
    },
    purple: {
      50: '#faf5ff',
      100: '#f3e8ff',
      200: '#e9d5ff',
      300: '#d8b4fe',
      400: '#c084fc',
      500: '#a855f7',
      600: '#9333ea',
      700: '#7c3aed',
      800: '#6b21a8',
      900: '#581c87'
    },
    amber: {
      50: '#fffbeb',
      100: '#fef3c7',
      200: '#fde68a',
      300: '#fcd34d',
      400: '#fbbf24',
      500: '#f59e0b',
      600: '#d97706',
      700: '#b45309',
      800: '#92400e',
      900: '#78350f'
    }
  };

  return themes[theme];
};

// Helper function to get theme-specific completion colors for calendar
export const getCompletionColors = (theme: Theme, rate: number) => {
  const colors = getThemeColors(theme);
  
  if (rate === 0) return { backgroundColor: '#e5e7eb', color: '#374151' };
  if (rate <= 0.2) return { backgroundColor: colors[200], color: colors[800] };
  if (rate <= 0.4) return { backgroundColor: colors[300], color: colors[800] };
  if (rate <= 0.6) return { backgroundColor: colors[400], color: colors[900] };
  if (rate <= 0.8) return { backgroundColor: colors[500], color: '#ffffff' };
  return { backgroundColor: colors[600], color: '#ffffff' };
};

// Helper function to apply theme colors to progress bars
export const getProgressBarColor = (theme: Theme) => {
  const colors = getThemeColors(theme);
  return { backgroundColor: colors[500] };
};

// Helper function for theme-specific hover states
export const getHoverColor = (theme: Theme, isDark: boolean = false) => {
  const colors = getThemeColors(theme);
  return { backgroundColor: isDark ? colors[800] : colors[100] };
};

// Helper function to get button styles
export const getButtonStyles = (theme: Theme, variant: 'primary' | 'secondary' = 'primary') => {
  const colors = getThemeColors(theme);
  
  if (variant === 'primary') {
    return {
      backgroundColor: colors[600],
      color: '#ffffff',
      ':hover': {
        backgroundColor: colors[700]
      }
    };
  }
  
  return {
    backgroundColor: colors[100],
    color: colors[800],
    ':hover': {
      backgroundColor: colors[200]
    }
  };
};

// Helper function to get icon color
export const getIconColor = (theme: Theme) => {
  const colors = getThemeColors(theme);
  return { color: colors[500] };
};

// Helper function to get text color
export const getTextColor = (theme: Theme, variant: 'primary' | 'light' | 'dark' = 'primary') => {
  const colors = getThemeColors(theme);
  
  switch (variant) {
    case 'light':
      return { color: colors[400] };
    case 'dark':
      return { color: colors[700] };
    default:
      return { color: colors[600] };
  }
};

// Helper function to get border color
export const getBorderColor = (theme: Theme) => {
  const colors = getThemeColors(theme);
  return { borderColor: colors[500] };
};

// Helper function to get focus ring color
export const getFocusRingColor = (theme: Theme) => {
  const colors = getThemeColors(theme);
  return { 
    '--tw-ring-color': colors[500],
    '--tw-ring-opacity': '0.5'
  };
};